var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { convertInMatrix, formatValue, getRepresentativeValue } from '../utils.js';
/**
 * A TransactionsDataTableBuilder is used to setup and build two-dimensional arrays containing transactions.
 *
 * @public
 */
export class TransactionsDataTableBuilder {
    constructor(book, transactions, account) {
        this.shouldFormatDates = false;
        this.shouldFormatValues = false;
        this.shouldAddUrls = false;
        this.shouldAddProperties = false;
        this.shouldAddIds = false;
        this.shouldAddRecordedAt = true;
        this.shouldAddHiddenProperties = false;
        this.book = book;
        this.transactions = transactions;
        this.account = account;
    }
    /**
     * Defines whether the dates should be formatted, based on date pattern of the [[Book]].
     *
     * @param format - Whether to format dates
     *
     * @returns This builder with respective formatting option, for chaining.
     */
    formatDates(format) {
        this.shouldFormatDates = format;
        return this;
    }
    /**
     * Defines whether amounts should be formatted based on [[DecimalSeparator]] of the [[Book]].
     *
     * @param format - Whether to format values
     *
     * @returns This builder with respective formatting option, for chaining.
     */
    formatValues(format) {
        this.shouldFormatValues = format;
        return this;
    }
    /**
     * Defines whether to include attachments and url links.
     *
     * @param include - Whether to include URLs
     *
     * @returns This builder with respective option, for chaining.
     */
    urls(include) {
        this.shouldAddUrls = include;
        return this;
    }
    /**
     * Defines whether to include custom transaction properties.
     *
     * @param include - Whether to include properties
     *
     * @returns This builder with respective option, for chaining.
     */
    properties(include) {
        this.shouldAddProperties = include;
        return this;
    }
    /**
     * Defines whether to include transaction ids and remote ids.
     *
     * @param include - Whether to include ids
     *
     * @returns This builder with respective option, for chaining.
     */
    ids(include) {
        this.shouldAddIds = include;
        return this;
    }
    /**
     * Defines whether to include the "Recorded at" column.
     *
     * @param include - Whether to include the recorded at column
     *
     * @returns This builder with respective option, for chaining.
     */
    recordedAt(include) {
        this.shouldAddRecordedAt = include;
        return this;
    }
    /**
     * Defines whether to include hidden properties (keys ending with underscore "_").
     * Only relevant when {@link properties} is enabled.
     * Default is false — hidden properties are excluded.
     *
     * @param include - Whether to include hidden properties
     *
     * @returns This builder with respective option, for chaining.
     */
    hiddenProperties(include) {
        this.shouldAddHiddenProperties = include;
        return this;
    }
    /**
     * @deprecated Use {@link urls} instead.
     */
    includeUrls(include) {
        return this.urls(include);
    }
    /**
     * @deprecated Use {@link properties} instead.
     */
    includeProperties(include) {
        return this.properties(include);
    }
    /**
     * @deprecated Use {@link ids} instead.
     */
    includeIds(include) {
        return this.ids(include);
    }
    /**
     * Gets the account used to filter transactions, when applicable.
     *
     * @returns The account, when filtering by a single account.
     */
    getAccount() {
        return this.account;
    }
    /**
     * Builds a two-dimensional array containing all transactions.
     *
     * @returns A promise resolving to a two-dimensional array containing all transactions
     */
    build() {
        return __awaiter(this, void 0, void 0, function* () {
            const headerLine = this.getHeaderLine();
            const dataTable = yield this.get2DArray_();
            if (dataTable.length > 0) {
                dataTable.splice(0, 0, headerLine);
                convertInMatrix(dataTable);
                return dataTable;
            }
            else {
                return [headerLine];
            }
        });
    }
    getHeaderLine() {
        const headerLine = [];
        if (this.shouldAddIds) {
            headerLine.push('Transaction Id');
        }
        headerLine.push('Status');
        headerLine.push('Date');
        headerLine.push('Origin');
        headerLine.push('Destination');
        headerLine.push('Description');
        headerLine.push('Amount');
        if (this.shouldShowBalances()) {
            headerLine.push('Balance');
        }
        if (this.shouldAddRecordedAt) {
            headerLine.push('Recorded at');
        }
        if (this.shouldAddProperties) {
            for (const key of this.getPropertyKeys()) {
                headerLine.push(key);
            }
        }
        if (this.shouldAddIds) {
            for (const remoteIdHeader of this.getRemoteIdHeaders()) {
                headerLine.push(remoteIdHeader);
            }
        }
        if (this.shouldAddUrls) {
            for (const attachmentHeader of this.getAttachmentHeaders()) {
                headerLine.push(attachmentHeader);
            }
        }
        return headerLine;
    }
    get2DArray_() {
        return __awaiter(this, void 0, void 0, function* () {
            const dataTable = [];
            for (const transaction of this.transactions) {
                const line = [];
                if (this.shouldAddIds) {
                    line.push(transaction.getId());
                }
                line.push(transaction.getStatus());
                if (this.shouldFormatDates) {
                    line.push(transaction.getDateFormatted());
                }
                else {
                    line.push(transaction.getDateObject());
                }
                line.push(yield transaction.getCreditAccountName());
                line.push(yield transaction.getDebitAccountName());
                line.push(transaction.getDescription());
                let amount = transaction.getAmount();
                if (amount != null) {
                    if (this.shouldShowBalances() && this.account) {
                        const isCreditOnTx = yield this.isCreditOnTransaction_(transaction, this.account);
                        amount = getRepresentativeValue(amount, !isCreditOnTx);
                    }
                    if (this.shouldFormatValues) {
                        line.push(formatValue(amount, this.book.getDecimalSeparator(), this.book.getFractionDigits()));
                    }
                    else {
                        line.push(amount.toNumber());
                    }
                }
                else {
                    line.push('');
                }
                if (this.shouldShowBalances()) {
                    const accountBalance = yield transaction.getAccountBalance();
                    if (accountBalance != null) {
                        if (this.shouldFormatValues) {
                            line.push(formatValue(accountBalance, this.book.getDecimalSeparator(), this.book.getFractionDigits()));
                        }
                        else {
                            line.push(accountBalance.toNumber());
                        }
                    }
                    else {
                        line.push('');
                    }
                }
                if (this.shouldAddRecordedAt) {
                    if (this.shouldFormatDates) {
                        line.push(transaction.getCreatedAtFormatted());
                    }
                    else {
                        line.push(transaction.getCreatedAt());
                    }
                }
                if (this.shouldAddProperties) {
                    this.addPropertiesToLine(line, transaction);
                }
                if (this.shouldAddIds) {
                    this.addRemoteIdsToLine(line, transaction);
                }
                if (this.shouldAddUrls) {
                    this.addUrlsToLine(line, transaction);
                }
                dataTable.push(line);
            }
            return dataTable;
        });
    }
    shouldShowBalances() {
        return this.account != null && this.account.isPermanent() === true;
    }
    isCreditOnTransaction_(transaction, account) {
        return __awaiter(this, void 0, void 0, function* () {
            const creditAccount = yield transaction.getCreditAccount();
            if (account == null || creditAccount == null) {
                return false;
            }
            return creditAccount.getId() === account.getId();
        });
    }
    getPropertyKeys() {
        if (this.propertyKeys == null) {
            this.propertyKeys = [];
            for (const transaction of this.transactions) {
                for (const key of transaction.getPropertyKeys()) {
                    if (!this.shouldAddHiddenProperties && key.endsWith('_')) {
                        continue;
                    }
                    if (this.propertyKeys.indexOf(key) <= -1) {
                        this.propertyKeys.push(key);
                    }
                }
            }
            this.propertyKeys = this.propertyKeys.sort();
        }
        return this.propertyKeys;
    }
    getAttachmentHeaders() {
        if (this.attachmentHeaders == null) {
            this.attachmentHeaders = [];
            for (const transaction of this.transactions) {
                const urls = this.getUrls(transaction);
                if (urls.length > this.attachmentHeaders.length) {
                    this.attachmentHeaders = [];
                    urls.forEach(() => this.attachmentHeaders.push('Attachment'));
                }
            }
        }
        return this.attachmentHeaders;
    }
    getRemoteIdHeaders() {
        if (this.remoteIdHeaders == null) {
            this.remoteIdHeaders = [];
            for (const transaction of this.transactions) {
                const remoteIds = transaction.getRemoteIds();
                if (remoteIds && remoteIds.length > this.remoteIdHeaders.length) {
                    this.remoteIdHeaders = [];
                    remoteIds.forEach(() => this.remoteIdHeaders.push('Remote Id'));
                }
            }
        }
        return this.remoteIdHeaders;
    }
    addPropertiesToLine(line, transaction) {
        const lineLength = line.length;
        for (const key of this.getPropertyKeys()) {
            line.push('');
        }
        for (const key of transaction.getPropertyKeys()) {
            if (!this.shouldAddHiddenProperties && key.endsWith('_')) {
                continue;
            }
            const index = this.getPropertyKeys().indexOf(key) + lineLength;
            line[index] = transaction.getProperty(key);
        }
    }
    addRemoteIdsToLine(line, transaction) {
        const lineLength = line.length;
        for (const key of this.getRemoteIdHeaders()) {
            line.push('');
        }
        const remoteIds = transaction.getRemoteIds();
        if (remoteIds) {
            for (let index = lineLength; index < lineLength + remoteIds.length; index++) {
                line[index] = remoteIds[index - lineLength];
            }
        }
    }
    addUrlsToLine(line, transaction) {
        const lineLength = line.length;
        for (const key of this.getAttachmentHeaders()) {
            line.push('');
        }
        const urls = this.getUrls(transaction);
        for (let index = lineLength; index < lineLength + urls.length; index++) {
            line[index] = urls[index - lineLength];
        }
    }
    getUrls(transaction) {
        let urls = transaction.getUrls();
        if (urls == null) {
            urls = [];
        }
        const files = transaction.getFiles();
        if (files != null) {
            urls = urls.concat(files.map(f => f.getUrl()).filter((url) => url != null));
        }
        return urls;
    }
}
//# sourceMappingURL=TransactionsDataTableBuilder.js.map